import React, { Fragment, useMemo } from 'react';
import styled from '@emotion/styled';
import { format, set } from 'date-fns';
import { DatePicker, Select } from '@bucketplace/commerce-admin-ui';

import { useT } from '@/builder/i18n';

const DATE_FORMAT = 'yyyy-MM-dd';
const TIME_FORMAT = 'HH:mm';
const DURATION_LABELS: {
  labelKey: string,
  type: keyof Duration,
}[] = [
  { labelKey: 'Editor.duration.start', type: 'startAt' },
  { labelKey: 'Editor.duration.end', type: 'endAt' },
];

// ['00:00' ~ '23:30']
export const TIME_OPTIONS = Array.from({ length: 144 }).map((_, i) => {
  const hh: string = `${Math.floor(i / 6)}`.padStart(2, '0');
  const mm: string = `${(i % 6) * 10}`.padStart(2, '0');
  return {
    text: `${hh}:${mm}`,
    value: `${hh}:${mm}`,
  };
});

export interface Duration {
  startAt?: Date;
  endAt?: Date;
}

export function DurationInput({
  value = {},
  onChange,
}: {
  value: Duration,
  onChange: (value: Duration) => void,
}) {
  const t = useT();
  const time = useMemo(() => {
    return {
      startAt: value.startAt ? format(value.startAt, TIME_FORMAT) : '00:00',
      endAt: value.endAt ? format(value.endAt, TIME_FORMAT) : '00:00',
    };
  }, [value]);

  function handleDateChange(type: keyof Duration, duration: [Date | null, Date | null]) {
    const nextDate = duration[0];
    const { [type]: prevValue, ...rest } = value;
    const [hours, minutes] = time[type].split(/:/);

    onChange({
      ...rest,
      ...nextDate && {
        [type]: set(nextDate, {
          hours: parseInt(hours, 10) || 0,
          minutes: parseInt(minutes, 10) || 0,
          milliseconds: 0,
        }),
      },
    });
  }

  const handleTimeChange = (type: keyof Duration, newTime: string) => {
    const targetTime = value[type]!;
    const [hours, minutes] = newTime.split(/:/);

    onChange({
      ...value,
      [type]: set(targetTime, {
        hours: parseInt(hours, 10),
        minutes: parseInt(minutes, 10),
      }),
    });
  };

  return (
    <InputGrid>
      {
        DURATION_LABELS.map((duration) => (
          <Fragment key={duration.type}>
            <InputLabel>{ t(duration.labelKey) }</InputLabel>
            <DatePicker
              data-testid={`duration_${duration.type}_date`}
              selected={value[duration.type]}
              placeholderText={t('Editor.duration.datePlaceholder')}
              dateFormat={DATE_FORMAT}
              onChange={(date) => handleDateChange(duration.type, date)}
            />
            <Select
              data-testid={`duration_${duration.type}_time`}
              value={time[duration.type]}
              size={32}
              options={TIME_OPTIONS}
              disabled={!value[duration.type]}
              onValue={(value) => handleTimeChange(duration.type, value as string)}
            />
          </Fragment>
        ))
      }
    </InputGrid>
  );
}

const InputGrid = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: 25px 1fr 1fr;
  gap: 4px;
`;

const InputLabel = styled.span`
  color: ${({ theme }) => theme.base.neutral600};
  font-size: 11px;
  font-weight: 500;
`;
